import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE_URL, BIZ_ID, WEBSITE_ID, DEFAULT_IMAGE } from "../seo";

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (content == null) return el && el.remove();
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!href) return el && el.remove();
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = href;
};

const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!data) return el && el.remove();
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const toUrl = (path) => `${SITE_URL}${(path.replace(/\/+$/, "") || "/")}`.replace(/\.com\/$/, ".com/");

// Per-page title, description, canonical, robots, Open Graph/Twitter and WebPage/Breadcrumb JSON-LD.
export const useSEO = ({ title, description, canonicalPath, noindex, status, crumbs, image }) => {
  const { pathname } = useLocation();
  const crumbKey = JSON.stringify(crumbs || null);

  useEffect(() => {
    if (!title) return;
    const url = toUrl(canonicalPath || pathname);
    const img = image || DEFAULT_IMAGE;
    document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "robots", noindex || status === 404 ? "noindex, follow" : "index, follow");
    setMeta("name", "prerender-status-code", status ? String(status) : null);
    setCanonical(status === 404 ? null : url);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", img);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", img);

    const list = crumbs ? JSON.parse(crumbKey) : null;
    setJsonLd("seo-webpage", status === 404 ? null : {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      url,
      name: title,
      description,
      inLanguage: "en-US",
      isPartOf: { "@id": WEBSITE_ID },
      about: { "@id": BIZ_ID },
      primaryImageOfPage: img,
    });
    setJsonLd("seo-breadcrumb", list && {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [{ name: "Home", path: "/" }, ...list].map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.name,
        item: toUrl(c.path),
      })),
    });
  }, [title, description, canonicalPath, noindex, status, pathname, crumbKey, image, crumbs]);
};

export default useSEO;
